import { Box } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { obratModel, TObratGroup } from "../../../api/models/obratModel";
import axios from "../../../axios/axios";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { Obrat } from "../../../ts/Obraty";
import { Column } from "../../../ts/UI";
import Checkbox from "../../ui/Checkbox/Checkbox";
import { ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { MultiLineCell } from "../../ui/LazyDataGrid/MultiLineCell";
import Table from "../../ui/Table/Table";
import { Filter } from "./Obraty";
import "./ObratyTable.scss";
import { Mesic, MesicString } from "./utils";
import { OldObratyTable } from "./OldObratyTable";
import ExportButton from "../../../exports/ExportButton";

const oldObratyColumns: Column[] = [
  {
    key: "_id",
    visible: false,
  },
  {
    key: "id_klienta",
    title: "ID - Klient",
  },
  {
    key: "spolocnost",
    title: "SPOLOČNOSŤ",
    editable: false,
  },
  {
    key: "rok",
    title: "ROK",
    type: "number",
  },
  {
    key: "mesiac",
    title: "MESIAC",
    type: "number",
  },
  {
    key: "suma",
    title: "SPOLU",
    type: "number",
  },
  {
    key: "actions",
    width: "5%",
  },
];

const getObratyColumns = (mesice: (number | undefined)[]): Column[] => {
  const getMesice = (): Column[] => {
    let mesiceActive = mesice.length > 0;

    if (!mesiceActive)
      return [
        {
          key: "leden",
          title: "Jan",
        },
        {
          key: "unor",
          title: "Feb",
        },
        {
          key: "brezen",
          title: "Mar",
        },
        {
          key: "duben",
          title: "Apr",
        },
        {
          key: "kveten",
          title: "Máj",
        },
        {
          key: "cerven",
          title: "Jún",
        },
        {
          key: "cervenec",
          title: "Júl",
        },
        {
          key: "srpen",
          title: "Aug",
        },
        {
          key: "zari",
          title: "Sep",
        },
        {
          key: "rijen",
          title: "Okt",
        },
        {
          key: "listopad",
          title: "Nov",
        },
        {
          key: "prosinec",
          title: "Dec",
        },
      ];

    return mesice.map((m) => {
      if (m === 1)
        return {
          key: "leden",
          title: "Jan",
        };
      if (m === 2)
        return {
          key: "unor",
          title: "Feb",
        };
      if (m === 3)
        return {
          key: "brezen",
          title: "Mar",
        };
      if (m === 4)
        return {
          key: "duben",
          title: "Apr",
        };
      if (m === 5)
        return {
          key: "kveten",
          title: "Máj",
        };
      if (m === 6)
        return {
          key: "cerven",
          title: "Jún",
        };
      if (m === 7)
        return {
          key: "cervenec",
          title: "Júl",
        };
      if (m === 8)
        return {
          key: "srpen",
          title: "Aug",
        };
      if (m === 9)
        return {
          key: "zari",
          title: "Sep",
        };
      if (m === 10)
        return {
          key: "rijen",
          title: "Okt",
        };
      if (m === 11)
        return {
          key: "listopad",
          title: "Nov",
        };
      if (m === 12)
        return {
          key: "prosinec",
          title: "Dec",
        };

      return {
        key: "",
        title: "",
      };
    });
  };

  return [
    {
      key: "_id",
      visible: false,
    },
    {
      key: "id_klienta",
      title: "ID - Klient",
    },
    {
      key: "spolocnost",
      title: "SPOLOČNOSŤ",
    },
    {
      key: "rok",
      title: "ROK",
    },
    ...getMesice(),
    {
      key: "spolu",
      title: "SPOLU",
      editable: false,
    },
  ];
};

const sumaForMesic = (
  mesic: MesicString,
  mesice: Mesic[]
): number | undefined => {
  let m = mesice.find((v) => v.nazev === mesic);

  if (m && m.suma) return m.suma;

  return undefined;
};

interface Props {
  filterCriteria: Filter[];
}

const ObratyTable = ({ filterCriteria }: Props) => {
  const [oldObraty, setOldObraty] = useState(false);

  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<TObratGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  useEffect(() => {
    const getObratyGroups = async () => {
      setIsLoading(true);
      await call(
        () =>
          obratModel.getFilteredObratyGroups({
            offset: (page * pageSize).toString(),
            limit: pageSize.toString(),
            sort: JSON.stringify(sortModel),
            query:
              filterCriteria.length > 0
                ? JSON.stringify(
                    filterCriteria.map((f) => ({
                      kontakt: f.kontakt?.value ? f.kontakt.value : undefined,
                      skupina: f.skupina?.value ? f.skupina.value : undefined,
                      obec: f.obec?.value ? f.obec.value : undefined,
                      okres: f.okres?.value ? f.okres.value : undefined,
                      obchodnik: f.obchodnik?.value
                        ? f.obchodnik.value
                        : undefined,
                      rok: f.rok?.value ? f.rok.value : undefined,
                      mesic: f.mesic?.value ? f.mesic.value : undefined,
                    }))
                  )
                : undefined,
          }),
        "Zobrazení obratov nebolo úspešné",
        null,
        ({ data, totalCount }) => {
          setData(data);
          setTotalCount(totalCount);
        },
        undefined,
        true
      );
      setIsLoading(false);
    };

    getObratyGroups();
  }, [page, filterCriteria, pageSize, sortModel]);

  const mesiaceToFilter: number[] = [];

  for (const f of filterCriteria) {
    if (f.mesic && !mesiaceToFilter.includes(f.mesic.value)) {
      const v = Number(f.mesic.value);

      if (!isNaN(v)) {
        mesiaceToFilter.push(Number(f.mesic.value));
      }
    }
  }

  const getMesiaceColumns = (): GridColDef<TObratGroup>[] => {
    const mesiaceColumns: GridColDef<TObratGroup>[] = [
      {
        field: "suma_1",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_1 ? Number(row.suma_1).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "JAN",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_2",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_2 ? Number(row.suma_2).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "FEB",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_3",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_3 ? Number(row.suma_3).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "MAR",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_4",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_4 ? Number(row.suma_4).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "APR",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_5",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_5 ? Number(row.suma_5).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "MÁJ",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_6",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_6 ? Number(row.suma_6).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "JŮN",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_7",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_7 ? Number(row.suma_7).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "JÚL",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_8",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_8 ? Number(row.suma_8).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "AUG",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_9",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_9 ? Number(row.suma_9).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "SEP",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_10",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_10 ? Number(row.suma_10).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "OKT",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_11",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_11 ? Number(row.suma_11).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "NOV",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
      {
        field: "suma_12",
        renderCell: ({ row }) => (
          <MultiLineCell sx={{ fontSize: 11 }}>
            {row.suma_12 ? Number(row.suma_12).toFixed(2) + " €" : ""}
          </MultiLineCell>
        ),
        headerName: "DEC",
        width: 55,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Number(value).toFixed(2) + " €" : "",
      },
    ];

    if (mesiaceToFilter.length === 0) {
      return mesiaceColumns;
    }

    const filteredCols: GridColDef<TObratGroup>[] = [];

    for (const mesiac of mesiaceToFilter) {
      filteredCols.push(mesiaceColumns[mesiac - 1]!);
    }

    return filteredCols;
  };

  return (
    <div className="obraty-table">
      <div className="sum-checkboxes-wrapper">
        <div>
          <Checkbox
            checked={oldObraty}
            onClick={() => setOldObraty(!oldObraty)}
          />
          <div>ZOBRAZIŤ JEDNOTLIVÉ ZÁZNAMY</div>
        </div>
      </div>
      <Box sx={{ display: oldObraty ? "none" : "block" }}>
        <LazyDataGrid<TObratGroup>
          id="obraty"
          rows={data}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          isLoading={isLoading}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          columns={[
            { field: "id_klienta", headerName: "ID - KLIENT", width: 90 },
            {
              field: "kontakt",
              headerName: "SPOLOČNOSŤ",
              width: 150,
              renderCell: ({ row }) => (
                <MultiLineCell>{row.kontakt?.nazev ?? ""}</MultiLineCell>
              ),
            },
            { field: "rok", headerName: "ROK", width: 55 },
            ...getMesiaceColumns(),
            {
              field: "suma_celkem_za_mesiace",
              sortable: true,
              headerName: "SPOLU",
              flex: 1,
              renderCell: ({ row }) =>
                row.suma_celkem_za_mesiace.toFixed(2) + " €",
            },
          ]}
        />
      </Box>
      <Box sx={{ display: oldObraty ? "block" : "none" }}>
        <OldObratyTable filterCriteria={filterCriteria} />
      </Box>
      <ExportButton
        mesiaceToFilter={mesiaceToFilter}
        type="obraty"
        query={
          filterCriteria.length > 0
            ? JSON.stringify(
                filterCriteria.map((f) => ({
                  kontakt: f.kontakt?.value ? f.kontakt.value : undefined,
                  skupina: f.skupina?.value ? f.skupina.value : undefined,
                  obec: f.obec?.value ? f.obec.value : undefined,
                  okres: f.okres?.value ? f.okres.value : undefined,
                  obchodnik: f.obchodnik?.value ? f.obchodnik.value : undefined,
                  rok: f.rok?.value ? f.rok.value : undefined,
                  mesic: f.mesic?.value ? f.mesic.value : undefined,
                }))
              )
            : undefined
        }
      />
    </div>
  );
};
export default ObratyTable;
